import React, { memo, useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle, useLayoutEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import clsx from "clsx";
import ResizeObserver from "react-resize-observer";

import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { Scrollbars } from "react-custom-scrollbars";

import { changeSortConfig, addFavSymbol, removeFavSymbol, changeSelectShowInfo, showPopupInfo, removeFindSymbol, changeSortConfigMenu } from "../redux/actions/sharedActions";
import { formatQtty, formatNumberToM, formatNumber } from "../helpers/utils";

export default memo(function PriceBoardTotalMode(props) {
   // Props var
   const { symbolInfos } = props;

   //
   return (
      <div className={"price-board"}>
         <TableBody symbolInfos={symbolInfos} />
      </div>
   );
});

//
const TableBody = forwardRef((props, ref) => {
   const { symbolInfos, onScroll } = props;
   //
   const { t } = useTranslation();
   const dispatch = useDispatch();
   const findSymbol = useSelector((state) => state.gShared.findSymbol);
   const priceBoardSortConfig = useSelector((state) => state.gShared.priceBoardSortConfig);
   const crrSortColumn = priceBoardSortConfig ? priceBoardSortConfig.column ?? "" : "";
   const crrSortType = priceBoardSortConfig ? priceBoardSortConfig.type ?? "" : "";
   //
   const refMatch = useRef(null);
   const refBid = useRef(null);
   const refAsk = useRef(null);
   const refTable = useRef(null);
   const refScroll = useRef(null);

   //
   const [headerRowHeight, setHeaderRowHeight] = useState(0);
   const [headerRowPos, setHeaderRowPos] = useState(null);

   const [matchPricePos, setMatchPricePos] = useState(null);
   const [matchPctChangePos, setMatchPctChangePos] = useState(null);

   const [bidQtty1Pos, setBidQtty1Pos] = useState(null);
   const [bidPrice1Pos, setBidPrice1Pos] = useState(null);

   const [askQtty1Pos, setAskQtty1Pos] = useState(null);
   const [askPrice1Pos, setAskPrice1Pos] = useState(null);

   const configSortColumnMenu = useSelector((state) => state.gShared.configSortColumnMenu);

   const configColumnNameTable = {
      symbolColAsc: "code",
      symbolColDesc: "code",
      totalQttyColAsc: "totalTradedQttyNM",
      totalQttyColDesc: "totalTradedQttyNM",
      foreignBuyColAsc: "buyForeignQtty",
      foreignBuyColDesc: "buyForeignQtty",
      foreignSellColAsc: "sellForeignQtty",
      foreignSellColDesc: "sellForeignQtty",
   };

   useEffect(() => {
      changeSortConfigMenu(dispatch, configColumnNameTable, configSortColumnMenu);
   }, [configSortColumnMenu]);

   //
   useImperativeHandle(ref, () => ({
      scrollBody: refScroll.current,
      scrollToItem: (itemIdx) => {
         if (refTable.current) {
            refTable.current.scrollToItem(itemIdx);
         }
      },
   }));

   //
   const _handleClickSort = (colName, dataType) => {
      changeSortConfig(dispatch, crrSortColumn, crrSortType, colName, dataType);
   };

   const _handleScroll = useCallback(
      ({ target }) => {
         const { scrollTop, scrollLeft } = target;
         if (refTable.current) {
            refTable.current.scrollTo(scrollTop);
         }
         //
         if (onScroll) {
            try {
               onScroll({ target });
            } catch (error) {}
         }
      },
      [refTable.current]
   );

   const _handleTableScroll = useCallback(
      ({ scrollDirection, scrollOffset, scrollUpdateWasRequested }) => {
         if (refScroll.current) {
            refScroll.current.scrollTop(scrollOffset);
         }
         if (findSymbol && findSymbol.code && findSymbol.code !== "") {
            removeFindSymbol(dispatch, "");
         }
      },
      [refScroll.current]
   );

   const _scrollToSymbol = () => {
      if (refTable.current) {
         if (findSymbol && findSymbol.code && findSymbol.code !== "" && symbolInfos && symbolInfos.length > 0) {
            const _idx = symbolInfos.findIndex((e) => e.code === findSymbol.code);
            if (_idx >= 0) {
               refTable.current.scrollToItem(_idx, "start");
            }
         }
      } else {
         setTimeout(() => {
            _scrollToSymbol();
         }, 100);
      }
   };

   const _calcDivMatchPosition = () => {
      if (refMatch.current !== null) {
         if (headerRowPos && matchPricePos && matchPctChangePos) {
            const _left = matchPricePos.x - headerRowPos.x;
            const _width = matchPctChangePos.x - matchPricePos.x + matchPctChangePos.width;
            refMatch.current.style.left = _left + "px";
            refMatch.current.style.width = _width + "px";
         }
      } else {
         setTimeout(() => {
            _calcDivMatchPosition();
         }, 500);
      }
   };

   const _calcDivBidPosition = () => {
      if (refBid.current !== null) {
         if (headerRowPos && bidQtty1Pos && bidPrice1Pos) {
            const _left = bidQtty1Pos.x - headerRowPos.x;
            const _width = bidPrice1Pos.x - bidQtty1Pos.x + bidPrice1Pos.width;
            refBid.current.style.left = _left + "px";
            refBid.current.style.width = _width + "px";
         }
      } else {
         setTimeout(() => {
            _calcDivBidPosition();
         }, 500);
      }
   };

   const _calcDivAskPosition = () => {
      if (refAsk.current !== null) {
         if (headerRowPos && askQtty1Pos && askPrice1Pos) {
            const _left = askPrice1Pos.x - headerRowPos.x;
            const _width = askQtty1Pos.x - askPrice1Pos.x + askQtty1Pos.width;
            refAsk.current.style.left = _left + "px";
            refAsk.current.style.width = _width + "px";
         }
      } else {
         setTimeout(() => {
            _calcDivAskPosition();
         }, 500);
      }
   };

   //
   useEffect(() => {
      _scrollToSymbol();
   }, [findSymbol.counter, symbolInfos.length]);

   useEffect(() => {
      _calcDivMatchPosition();
   }, [headerRowPos, matchPricePos, matchPctChangePos]);

   useEffect(() => {
      _calcDivBidPosition();
   }, [headerRowPos, bidQtty1Pos, bidPrice1Pos]);

   useEffect(() => {
      _calcDivAskPosition();
   }, [headerRowPos, askQtty1Pos, askPrice1Pos]);

   //
   return (
      <AutoSizer>
         {({ height, width }) => {
            return (
               <Scrollbars
                  ref={refScroll}
                  autoHide={true}
                  autoHeight={true}
                  autoHeightMax={height}
                  onScroll={_handleScroll}
                  style={{ width: width, position: "relative" }}
                  renderTrackHorizontal={(props) => <div {...props} className="scroll-track-horizontal" />}
                  renderTrackVertical={(props) => <div {...props} className="scroll-track-vertical" />}
                  renderThumbHorizontal={(props) => <div {...props} className="scroll-thumb-horizontal" />}
                  renderThumbVertical={(props) => <div {...props} className="scroll-thumb-vertical" />}
                  renderView={(props) => <div {...props} className="scroll-view" />}
                  className={"scroll-container"}
               >
                  <div style={{ width: "100%", position: "sticky", top: 0, zIndex: 1 }}>
                     <div className="prc-row-head prc-row-head-spec">
                        <ResizeObserver
                           onResize={(rect) => {
                              setHeaderRowPos(rect);
                           }}
                           onPosition={(rect) => {
                              setHeaderRowPos(rect);
                           }}
                           onReflow={(rect) => {
                              setHeaderRowPos(rect);
                           }}
                        />

                        <div className={"prc-col mw-40"}></div>
                        <div className={clsx("prc-col4 text-center mw-75 mxw-75 th-sort", crrSortColumn === "code" ? "th-sort-" + crrSortType : "")} onClick={() => _handleClickSort("code")}>
                           {t("maCK")}
                        </div>
                        <div
                           className={clsx("prc-col2 text-center mw-50 th-sort", crrSortColumn === "highestPrice" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("highestPrice", "number")}
                        >
                           {t("caoNhat")}
                        </div>
                        <div
                           className={clsx("prc-col2 text-center mw-50 th-sort", crrSortColumn === "lowestPrice" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("lowestPrice", "number")}
                        >
                           {t("thapNhat")}
                        </div>

                        <div className={clsx("prc-col6 text-center mw-140px")}>{t("klMuaBanChuDong")}</div>
                        <div
                           className={clsx("prc-col-rs prc-col2 text-center mw-50 th-sort", crrSortColumn === "matchPrice" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("matchPrice", "number")}
                        >
                           {t("gia")}
                           <ResizeObserver
                              onResize={(rect) => {
                                 setMatchPricePos(rect);
                              }}
                              onPosition={(rect) => {
                                 setMatchPricePos(rect);
                              }}
                              onReflow={(rect) => {
                                 setMatchPricePos(rect);
                              }}
                           />
                        </div>
                        <div
                           className={clsx("prc-col-rs prc-col2 text-center mw-50 th-sort", crrSortColumn === "changePrice" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("changePrice", "number")}
                        >
                           +/-
                        </div>
                        <div
                           className={clsx("prc-col-rs prc-col2 text-center mw-55 th-sort", crrSortColumn === "pctChangePrice" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("pctChangePrice", "number")}
                        >
                           %
                           <ResizeObserver
                              onResize={(rect) => {
                                 setMatchPctChangePos(rect);
                              }}
                              onPosition={(rect) => {
                                 setMatchPctChangePos(rect);
                              }}
                              onReflow={(rect) => {
                                 setMatchPctChangePos(rect);
                              }}
                           />
                        </div>
                        <div ref={refMatch} className={"prc-col-cs"}>
                           {t("giaKhopGanNhat")}
                        </div>

                        <div
                           className={clsx("prc-col3 text-center mw-75 th-sort", crrSortColumn === "totalTradedQttyNM" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("totalTradedQttyNM", "number")}
                        >
                           {t("tongKLKhop")}
                        </div>
                        <div
                           className={clsx("prc-col4 text-center mw-85 th-sort", crrSortColumn === "totalTradedValueNM" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("totalTradedValueNM", "number")}
                        >
                           {t("tongGTKhop")}
                        </div>

                        <div
                           className={clsx("prc-col-rs prc-col2 text-center th-sort mw-60", crrSortColumn === "bidQtty1" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("bidQtty1", "number")}
                        >
                           {t("kl1")}
                           <ResizeObserver
                              onResize={(rect) => {
                                 setBidQtty1Pos(rect);
                              }}
                              onPosition={(rect) => {
                                 setBidQtty1Pos(rect);
                              }}
                              onReflow={(rect) => {
                                 setBidQtty1Pos(rect);
                              }}
                           />
                        </div>
                        <div
                           className={clsx("prc-col-rs prc-col2 text-center th-sort mw-50", crrSortColumn === "bidPrice1" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("bidPrice1", "number")}
                        >
                           {t("gia1")}
                           <ResizeObserver
                              onResize={(rect) => {
                                 setBidPrice1Pos(rect);
                              }}
                              onPosition={(rect) => {
                                 setBidPrice1Pos(rect);
                              }}
                              onReflow={(rect) => {
                                 setBidPrice1Pos(rect);
                              }}
                           />
                        </div>
                        <div ref={refBid} className={"prc-col-cs prc-col-bid"}>
                           {t("muaBid")}
                        </div>

                        <div
                           className={clsx("prc-col-rs prc-col2 text-center th-sort mw-50", crrSortColumn === "offerPrice1" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("offerPrice1", "number")}
                        >
                           {t("gia1")}
                           <ResizeObserver
                              onResize={(rect) => {
                                 setAskPrice1Pos(rect);
                              }}
                              onPosition={(rect) => {
                                 setAskPrice1Pos(rect);
                              }}
                              onReflow={(rect) => {
                                 setAskPrice1Pos(rect);
                              }}
                           />
                        </div>
                        <div
                           className={clsx("prc-col-rs prc-col2 text-center th-sort mw-60", crrSortColumn === "offerQtty1" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("offerQtty1", "number")}
                        >
                           {t("kl1")}
                           <ResizeObserver
                              onResize={(rect) => {
                                 setAskQtty1Pos(rect);
                              }}
                              onPosition={(rect) => {
                                 setAskQtty1Pos(rect);
                              }}
                              onReflow={(rect) => {
                                 setAskQtty1Pos(rect);
                              }}
                           />
                        </div>
                        <div ref={refAsk} className={"prc-col-cs prc-col-ask"}>
                           {t("banAsk")}
                        </div>

                        <div
                           className={clsx("prc-col3 text-center mw-70 th-sort", crrSortColumn === "buyForeignQtty" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("buyForeignQtty", "number")}
                        >
                           {t("klNNMua")}
                        </div>
                        <div
                           className={clsx("prc-col3 text-center mw-70 th-sort", crrSortColumn === "sellForeignQtty" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("sellForeignQtty", "number")}
                        >
                           {t("klNNBan")}
                        </div>
                        <div
                           className={clsx("prc-col3 text-center mw-70 th-sort", crrSortColumn === "subForeignQtty" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("subForeignQtty", "number")}
                        >
                           {t("klNNRong")}
                        </div>

                        <div
                           className={clsx("prc-col4 text-center mw-80 th-sort", crrSortColumn === "buyForeignValue" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("buyForeignValue", "number")}
                        >
                           {t("gtNNMua")}
                        </div>
                        <div
                           className={clsx("prc-col4 text-center mw-80 th-sort", crrSortColumn === "sellForeignValue" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("sellForeignValue", "number")}
                        >
                           {t("gtNNBan")}
                        </div>
                        <div
                           className={clsx("prc-col4 text-center mw-80 th-sort", crrSortColumn === "subForeignValue" ? "th-sort-" + crrSortType : "")}
                           onClick={() => _handleClickSort("subForeignValue", "number")}
                        >
                           {t("gtNNRong")}
                        </div>

                        <div className="prc-col3 text-center mw-80">{t("bieuDo1ThangGanNhat")}</div>
                     </div>
                     <ResizeObserver
                        onResize={(rect) => {
                           setHeaderRowHeight(rect.height);
                        }}
                     />
                  </div>
                  <ScrollContent symbolInfos={symbolInfos} height={height - headerRowHeight} onScroll={_handleTableScroll} ref={refTable} />
               </Scrollbars>
            );
         }}
      </AutoSizer>
   );
});

const ScrollContent = memo(
   forwardRef(function (props, ref) {
      const { symbolInfos, height, onScroll } = props;

      return (
         <FixedSizeList ref={ref} height={height} width={"100%"} itemCount={symbolInfos.length} itemSize={28} style={{ overflow: false }} onScroll={onScroll} overscanCount={30}>
            {({ index, style }) => {
               const _symbolInfo = symbolInfos[index];

               return <RowFlex className={"prc-row " + (index % 2 === 0 ? "odd" : "even")} style={{ ...style }} counter={_symbolInfo.counter} code={_symbolInfo.code} />;
            }}
         </FixedSizeList>
      );
   })
);

//
// import StockPriceHistChart from './stock-components/StockPriceHistChart';
const StockPriceHistChart = React.lazy(() => import("./stock-components/StockPriceHistChart"));

const RowFlex = function (props) {
   const { code } = props;

   //
   const dispatch = useDispatch();
   const prevSymbolCode = useRef("");

   const tabIsFocus = useSelector((state) => state.gShared.tabIsFocus);
   const counter = useSelector((state) => state.gSymbol.symbolInfos.find((e) => e.code === code).counter);
   const symbolInfo = useSelector((state) => state.gSymbol.symbolInfos.find((e) => e.code === code));
   const isFav = useSelector((state) => state.gShared.priceBoardFavSymbols.indexOf(symbolInfo.code) >= 0);

   const priceBoardIsOddlot = useSelector((state) => state.gShared.priceBoardIsOddlot);

   const _fireEvent = prevSymbolCode.current === symbolInfo.code && tabIsFocus;

   //
   const _addFavSymbol = useCallback((code) => {
      addFavSymbol(dispatch, code);
   }, []);

   const _removeFavSymbol = useCallback((code) => {
      removeFavSymbol(dispatch, code);
   }, []);

   const _handleRowClick = useCallback((event, code) => {
      if (code) {
         if (code + "_code" === event.target.id) {
            showPopupInfo(dispatch, "ST", code);
         } else if (code + "_star" === event.target.id) {
            //
         } else {
            changeSelectShowInfo(dispatch, "ST", code);
         }
      }
   }, []);

   //
   useEffect(() => {
      prevSymbolCode.current = symbolInfo.code;
   }, [symbolInfo.code]);

   //
   return (
      <div onClick={(event) => _handleRowClick(event, symbolInfo.code)} className={props.className} style={{ ...props.style }}>
         <div className={"prc-col text-center mw-40"}>
            {isFav ? (
               <span onClick={() => _removeFavSymbol(symbolInfo.code)} style={{ cursor: "pointer", lineHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                  <img id={symbolInfo.code + "_star"} className="imgstar" src="/static/img/icon/icon-star-yellow.svg" alt="img" />
                  &nbsp;
               </span>
            ) : (
               <span onClick={() => _addFavSymbol(symbolInfo.code)} style={{ cursor: "pointer", lineHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                  <img id={symbolInfo.code + "_star"} className="imgstar" src="/static/img/icon/icon-star.svg" alt="img" />
                  &nbsp;
               </span>
            )}
         </div>

         {priceBoardIsOddlot ? (
            <>
               <div className={clsx("prc-col4 flex1 text-left mw-75 mxw-75", symbolInfo.matchPriceOdd_color)} title={(symbolInfo.symbolName ?? "") + " - " + symbolInfo.marketName}>
                  <span id={symbolInfo.code + "_code"}>
                     {symbolInfo.code}
                     {symbolInfo.referenceStatus && symbolInfo.referenceStatus !== "" && symbolInfo.referenceStatus !== "0" ? "*" : ""}
                  </span>
               </div>
               <div className={clsx("prc-col2 mw-50 text-right")}></div>
               <div className={clsx("prc-col2 mw-50 text-right")}></div>

               <div className="prc-col6 d-flex mw-140px"></div>
               <div className={clsx("prc-col2 text-right mw-50 bg-smoke", symbolInfo.matchPriceOdd_color)}>
                  <HighlightSpan id={symbolInfo.code + "_matchPriceOdd"} value={symbolInfo.matchPriceOdd} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right mw-50 bg-smoke", symbolInfo.matchPriceOdd_color)}>
                  <HighlightSpan id={symbolInfo.code + "_changePriceOdd"} value={symbolInfo.changePriceOdd} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right mw-55 bg-smoke", symbolInfo.matchPriceOdd_color)}>
                  <HighlightSpan
                     id={symbolInfo.code + "_pctChangePriceOdd"}
                     value={symbolInfo.pctChangePriceOdd + (symbolInfo.pctChangePriceOdd && symbolInfo.pctChangePriceOdd !== "" ? "%" : "")}
                     fireEvent={_fireEvent}
                  />
               </div>

               <div className="prc-col3 text-right mw-75">
                  <HighlightSpan id={symbolInfo.code + "_totalTradedQttyOdd"} value={formatNumber(symbolInfo.totalTradedQttyOdd)} fireEvent={_fireEvent} />
               </div>
               <div className="prc-col4 text-right mw-85">
                  <HighlightSpan id={symbolInfo.code + "_totalTradedValueOdd"} value={formatNumberToM(symbolInfo.totalTradedValueOdd)} fireEvent={_fireEvent} />
               </div>

               <div className={clsx("prc-col2 text-right bg-smoke mw-60")}>
                  <HighlightSpan id={symbolInfo.code + "_bidQttyOdd1"} value={formatNumber(symbolInfo.bidQttyOdd1)} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right bg-smoke mw-50", symbolInfo.bidPriceOdd1_color)}>
                  <HighlightSpan id={symbolInfo.code + "_bidPriceOdd1"} value={symbolInfo.bidPriceOdd1} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right bg-smoke mw-50", symbolInfo.offerPriceOdd1_color)}>
                  <HighlightSpan id={symbolInfo.code + "_offerPriceOdd1"} value={symbolInfo.offerPriceOdd1} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right bg-smoke mw-60")}>
                  <HighlightSpan id={symbolInfo.code + "_offerQttyOdd1"} value={formatNumber(symbolInfo.offerQttyOdd1)} fireEvent={_fireEvent} />
               </div>

               <div className="prc-col3 text-right mw-70"></div>
               <div className="prc-col3 text-right mw-70"></div>
               <div className="prc-col3 text-right mw-70"></div>

               <div className="prc-col4 text-right mw-80"></div>
               <div className="prc-col4 text-right mw-80"></div>
               <div className="prc-col4 text-right mw-80"></div>

               <div className="prc-col3 mw-80"></div>
            </>
         ) : (
            <>
               <div className={clsx("prc-col4 flex1 text-left mw-75 mxw-75", symbolInfo.matchPrice_color)} title={(symbolInfo.symbolName ?? "") + " - " + symbolInfo.marketName}>
                  <span id={symbolInfo.code + "_code"}>
                     {symbolInfo.code}
                     {symbolInfo.referenceStatus && symbolInfo.referenceStatus !== "" && symbolInfo.referenceStatus !== "0" ? "*" : ""}
                  </span>
               </div>
               <div className={clsx("prc-col2 mw-50 text-right", symbolInfo.highestPrice_color)}>
                  <HighlightSpan id={symbolInfo.code + "_highestPrice"} value={symbolInfo.highestPrice} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 mw-50 text-right", symbolInfo.lowestPrice_color)}>
                  <HighlightSpan id={symbolInfo.code + "_lowestPrice"} value={symbolInfo.lowestPrice} fireEvent={_fireEvent} />
               </div>

               <div className="prc-col6 d-flex mw-140px">
                  <span className="flex-1 clr-up text-right">{formatQtty(symbolInfo.totalBuyQtty)}</span>
                  <span className="pdr-1 pdl-1">|</span>
                  <span className="flex-1 clr-down">{formatQtty(symbolInfo.totalSellQtty)}</span>
               </div>
               <div className={clsx("prc-col2 text-right mw-50 bg-smoke", symbolInfo.matchPrice_color)}>
                  <HighlightSpan id={symbolInfo.code + "_matchPrice"} value={symbolInfo.matchPrice} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right mw-50 bg-smoke", symbolInfo.matchPrice_color)}>
                  <HighlightSpan id={symbolInfo.code + "_changePrice"} value={symbolInfo.changePrice} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right mw-55 bg-smoke", symbolInfo.matchPrice_color)}>
                  <HighlightSpan
                     id={symbolInfo.code + "_pctChangePrice"}
                     value={symbolInfo.pctChangePrice + (symbolInfo.pctChangePrice && symbolInfo.pctChangePrice !== "" ? "%" : "")}
                     fireEvent={_fireEvent}
                  />
               </div>

               <div className="prc-col3 text-right mw-75">
                  <HighlightSpan id={symbolInfo.code + "_totalTradedQttyNM"} value={formatQtty(symbolInfo.totalTradedQttyNM)} fireEvent={_fireEvent} />
               </div>
               <div className="prc-col4 text-right mw-85">
                  <HighlightSpan id={symbolInfo.code + "_totalTradedValueNM"} value={formatNumberToM(symbolInfo.totalTradedValueNM)} fireEvent={_fireEvent} />
               </div>

               <div className={clsx("prc-col2 text-right bg-smoke mw-60")}>
                  <HighlightSpan id={symbolInfo.code + "_bidQtty1"} value={formatQtty(symbolInfo.bidQtty1)} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right bg-smoke mw-50", symbolInfo.bidPrice1_color)}>
                  <HighlightSpan id={symbolInfo.code + "_bidPrice1"} value={symbolInfo.bidPrice1} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right bg-smoke mw-50", symbolInfo.offerPrice1_color)}>
                  <HighlightSpan id={symbolInfo.code + "_offerPrice1"} value={symbolInfo.offerPrice1} fireEvent={_fireEvent} />
               </div>
               <div className={clsx("prc-col2 text-right bg-smoke mw-60")}>
                  <HighlightSpan id={symbolInfo.code + "_offerQtty1"} value={formatQtty(symbolInfo.offerQtty1)} fireEvent={_fireEvent} />
               </div>

               <div className="prc-col3 text-right mw-70">
                  <HighlightSpan id={symbolInfo.code + "_buyForeignQtty"} value={formatQtty(symbolInfo.buyForeignQtty)} fireEvent={_fireEvent} />
               </div>
               <div className="prc-col3 text-right mw-70">
                  <HighlightSpan id={symbolInfo.code + "_sellForeignQtty"} value={formatQtty(symbolInfo.sellForeignQtty)} fireEvent={_fireEvent} />
               </div>
               <div className="prc-col3 text-right mw-70">
                  <HighlightSpan id={symbolInfo.code + "_subForeignQtty"} value={formatQtty(symbolInfo.subForeignQtty)} fireEvent={_fireEvent} />
               </div>

               <div className="prc-col4 text-right mw-80">
                  <HighlightSpan id={symbolInfo.code + "_buyForeignValue"} value={formatNumberToM(symbolInfo.buyForeignValue)} fireEvent={_fireEvent} />
               </div>
               <div className="prc-col4 text-right mw-80">
                  <HighlightSpan id={symbolInfo.code + "_sellForeignValue"} value={formatNumberToM(symbolInfo.sellForeignValue)} fireEvent={_fireEvent} />
               </div>
               <div className="prc-col4 text-right mw-80">
                  <HighlightSpan id={symbolInfo.code + "_subForeignValue"} value={formatNumberToM(symbolInfo.subForeignValue)} fireEvent={_fireEvent} />
               </div>

               <div className="prc-col3 mw-80">
                  <React.Suspense fallback={<span>...</span>}>
                     <StockPriceHistChart code={symbolInfo.code} />
                  </React.Suspense>
               </div>
            </>
         )}
      </div>
   );
};

//
export const HighlightSpan = memo(
   function (props) {
      const { highlightSelf } = props;
      const prevValue = useRef(props.value);

      useLayoutEffect(() => {
         let _timerId = -1,
            _timerAddId = -1;
         const _el = highlightSelf ? document.getElementById(props.id) : document.getElementById(props.id).parentElement;

         if (props.value !== prevValue.current) {
            prevValue.current = props.value;
            //
            if (_el && props.fireEvent) {
               _el.classList.remove("active_change");
               // _timerAddId =  setTimeout(() => {
               _el.classList.add("active_change");
               _timerId = setTimeout(() => {
                  _el.classList.remove("active_change");
               }, 300);
               // }, 1);
            }
         }

         return () => {
            clearTimeout(_timerId);
            clearTimeout(_timerAddId);
            try {
               if (_el) {
                  _el.classList.remove("active_change");
               }
            } catch (error) {}
         };
      });

      //
      return (
         <span style={{ ...props.style }} id={props.id} className={clsx(props.className, props.color)}>
            {props.value}
         </span>
      );
   },
   (prevProps, nextProps) => {
      if (prevProps.value === nextProps.value && prevProps.color === nextProps.color) {
         return true;
      }
      return false;
   }
);
